import { Container, Sprite, TilingSprite } from 'pixi.js';
import { getTexture, makeSprite } from '@/assets/resolver';
import { MAPS } from '@/config/maps';
import type { ThemeDef } from '@/config/themes';
import type { Rng } from '@/core/rng';
import type { Arena } from './arena';
import { placeDecor, DecorLayer } from './decor';
import { GateView } from './gate-view';
import type { Rect } from './geometry';

/** A spatial cell of static decor — culled as one unit. */
interface Cell {
  readonly x: number;
  readonly y: number;
  readonly views: Container[];
}

/** Extra reach of a cell's sprites past its square (largest decor half-size). */
const CELL_REACH = 200;
/** Wall mass margin past the room bounds (never shows bare background) */
const MASS_MARGIN = 1400;

/** Is URL-level baking disabled (`?bake=0` — Xbox/driver insurance)? */
function bakeEnabled(): boolean {
  const search = typeof location !== 'undefined' ? location.search : '';
  return !/[?&]bake=0\b/.test(search);
}

/**
 * The room's look (docs/04 environment themes): wall mass, tiled floors, static
 * decor in spatial cells — floor decals BAKED into one cached texture per
 * cell, crisp walls/rocks/props live — plus the gate shields. Cells and
 * floors are culled against the camera view every frame.
 */
export class ArenaView {
  readonly view = new Container();
  private readonly floors: Array<{ sprite: Container; rect: Rect }> = [];
  private readonly cells: Cell[] = [];
  private readonly gates: GateView[] = [];

  constructor(arena: Arena, theme: ThemeDef, rng: Rng) {
    const b = arena.bounds;
    const mass = new TilingSprite({
      texture: getTexture(theme.wallMass),
      width: b.w + MASS_MARGIN * 2,
      height: b.h + MASS_MARGIN * 2,
    });
    mass.position.set(b.x - MASS_MARGIN, b.y - MASS_MARGIN);
    this.view.addChild(mass);

    for (const p of arena.pieces) this.addFloor(theme, p.outer);
    for (const d of arena.doors) this.addFloor(theme, d.rect);

    const decalLayer = new Container();
    const liveLayer = new Container();
    const gateLayer = new Container();
    this.view.addChild(decalLayer, liveLayer, gateLayer);
    this.buildCells(placeDecor(arena, theme, rng), decalLayer, liveLayer);
    for (const door of arena.doors) {
      const g = new GateView(door, theme.gateTint);
      gateLayer.addChild(g.view);
      this.gates.push(g);
    }
  }

  private addFloor(theme: ThemeDef, r: Rect): void {
    const floor = new TilingSprite({ texture: getTexture(theme.ground), width: r.w, height: r.h });
    floor.position.set(r.x, r.y);
    // Tiles line up in WORLD space across pieces and doorways
    floor.tilePosition.set(-r.x, -r.y);
    this.view.addChild(floor);
    this.floors.push({ sprite: floor, rect: r });
  }

  private buildCells(
    items: ReturnType<typeof placeDecor>, decalLayer: Container, liveLayer: Container,
  ): void {
    const size = MAPS.bake.chunk;
    const byKey = new Map<string, { cell: Cell; decals: Container; live: Container }>();
    for (const it of items) {
      const cx = Math.floor(it.x / size);
      const cy = Math.floor(it.y / size);
      const key = `${cx},${cy}`;
      let slot = byKey.get(key);
      if (!slot) {
        const decals = new Container();
        const live = new Container();
        decalLayer.addChild(decals);
        liveLayer.addChild(live);
        slot = { cell: { x: cx * size, y: cy * size, views: [decals, live] }, decals, live };
        byKey.set(key, slot);
        this.cells.push(slot.cell);
      }
      const s: Sprite = makeSprite(it.key, it.frame);
      s.position.set(it.x, it.y);
      s.scale.set(it.scale);
      s.rotation = it.rotation;
      s.tint = it.tint;
      (it.layer === DecorLayer.Decal ? slot.decals : slot.live).addChild(s);
    }
    // Bake: every cell's floor decals collapse into one cached texture
    if (!bakeEnabled()) return;
    for (const slot of byKey.values()) {
      if (slot.decals.children.length > 0) {
        slot.decals.cacheAsTexture({ resolution: MAPS.bake.resolution, antialias: false });
      }
    }
  }

  /** Per render frame: gate animation + culling against the camera view. */
  render(cx: number, cy: number, viewW: number, viewH: number, time: number): void {
    const m = MAPS.bake.cullMargin;
    const x0 = cx - viewW / 2 - m;
    const x1 = cx + viewW / 2 + m;
    const y0 = cy - viewH / 2 - m;
    const y1 = cy + viewH / 2 + m;
    const size = MAPS.bake.chunk;
    for (const c of this.cells) {
      const vis = c.x + size + CELL_REACH > x0 && c.x - CELL_REACH < x1 &&
        c.y + size + CELL_REACH > y0 && c.y - CELL_REACH < y1;
      for (const v of c.views) v.visible = vis;
    }
    for (const f of this.floors) {
      const r = f.rect;
      f.sprite.visible = r.x + r.w > x0 && r.x < x1 && r.y + r.h > y0 && r.y < y1;
    }
    for (const g of this.gates) g.render(time);
  }

  /** Visible static cells right now (perf sanity read-out). */
  get visibleCells(): number {
    let n = 0;
    for (const c of this.cells) if (c.views[0]!.visible) n++;
    return n;
  }

  get cellCount(): number {
    return this.cells.length;
  }
}
